import { SortDir } from '__generated__';
import { linkClasses } from '@mui/material';
import { saveAs } from 'file-saver';
import { Parser } from 'json2csv';
import { memoize } from 'lodash-es';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GlobalHotKeys } from 'react-hotkeys';

import ExternalLinkIcon from '@sbu/assets.svg/src/external-link.svg';
import { Link } from '@sbu/components.link';
import { Loader } from '@sbu/components.loader';
import { useKanso } from '@sbu/contexts.kanso';
import { useLAPSearchUrl } from '@sbu/hooks.url';
import {
  Button,
  ClickAwayListener,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Tooltip,
  Typography,
} from '@sumologic/yama-ds-components';
import { AngleDownIcon, SortIcon } from '@sumologic/yama-ds-icons';

import { Sort, SortDirection, SortHeader } from 'shared/components/data/Sort';
import {
  getRecordSchemaVersion,
  Records as RecordDetails,
  RecordType,
} from 'shared/components/internal/Records';
import { useEntityFieldsAndIndicator } from 'shared/components/internal/Records/useEntityTypeRecordFields';
import { PropMapType, sort } from 'shared/utils/sort';

import css from './Records.module.scss';

const RECORD_SCHEMA_V3_TYPE = 'objectType';
const RECORD_SCHEMA_LEGACY_TYPE = 'type';

export const getRecordType = (record: any) => {
  // Determine the Record schema.
  const schemaVersion = getRecordSchemaVersion(record);

  const typeName =
    schemaVersion === 'v3' ? RECORD_SCHEMA_V3_TYPE : RECORD_SCHEMA_LEGACY_TYPE;

  const type = record.hasOwnProperty(typeName) ? record[typeName] : undefined;

  // Canary record type fields have a different casing than other Schema V3 type
  // fields.
  return type === 'canary' ? 'Canary' : type;
};

const parseCSV = (data: RecordType[], name = 'search.csv') => {
  try {
    const parser = new Parser({ flatten: true });
    const csv = parser.parse(data);

    const file = new File([csv], name, {
      type: 'text/plain;charset=utf-8',
    });

    return file;
  } catch (error) {}
};

const parseJSON = (data: RecordType[], name = 'search.json') => {
  try {
    return new File([JSON.stringify(data, null, 2)], name, {
      type: 'text/plain;charset=utf-8',
    });
  } catch (error) {}
};

type ExportFileTypes = 'csv' | 'json';
type RecordSortProps = 'timestamp';
type RecordSortsType = SortDirection<RecordSortProps>;

interface RecordsProps {
  allRecords: RecordType[] | null | undefined;
  disableSort?: boolean;
  end?: string;
  entityId: string;
  exploreInLogsQuery?: string;
  limit: number;
  recordCount: number;
  loading?: boolean;
  query?: string;
  start?: string;
}
interface RecordSortPropsType {
  [name: string]: {
    displayValue: string;
    name: RecordSortProps;
  };
}

interface TypesCount {
  [key: string]: number;
}

const sortProps: RecordSortPropsType = {
  timestamp: { displayValue: 'Timestamp', name: 'timestamp' },
};

export function parseRecords(allRecordsJson: string): RecordType[] | null {
  let records: any[] | null = [];

  try {
    records = JSON.parse(allRecordsJson);
  } catch {
    records = null;
  }

  return records as RecordType[];
}

// Timestamps are represented as numeric UNIX timestamp values.
const PROP_MAP: PropMapType = {
  timestamp: 'number',
};

const sortRecords = memoize((records: any[], sorts: RecordSortsType[]) =>
  sort(records, sorts, PROP_MAP)
);

const countRecordTypes = (allRecords: any[]): TypesCount => {
  const typesCount: TypesCount = {};

  allRecords.forEach((record) => {
    const type = getRecordType(record);

    const typeCount = typesCount[type];
    typesCount[type] = typeCount ? typeCount + 1 : 1;
  });
  return typesCount;
};

// complexity 12
// eslint-disable-next-line complexity
export function Records({
  allRecords,
  disableSort,
  end,
  entityId,
  exploreInLogsQuery,
  limit,
  recordCount,
  loading = false,
  query,
  start,
}: RecordsProps) {
  const recordsDisplayRef = useRef<HTMLDivElement>(null);
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [searchQueryMenuAnchorEl, setSearchQueryMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const sortMenuVisible = Boolean(sortMenuAnchorEl);
  const searchQueryMenuVisible = Boolean(searchQueryMenuAnchorEl);
  const exportMenuVisible = Boolean(exportMenuAnchorEl);

  const [expanded, setExpanded] = useState({});
  // The upper bound for the Record slice
  const [visibleIndex, setVisibleIndex] = useState<number>();
  const [sortedRecords, setSortedRecords] = useState<RecordType[]>([]);
  const [sorts, setSorts] = useState<RecordSortsType[]>([
    {
      prop: 'timestamp',
      direction: SortDir.Desc,
    },
  ]);

  const { entityTypeFieldsAndIndicator } =
    useEntityFieldsAndIndicator(entityId);

  // The first part checks if CIP is still sending new batches of Records.
  // The second part checks if the number of Records we receive matches the count
  // we get from the Signals/CIP API.
  const recordsLoaded =
    !loading || (sortedRecords.length === recordCount && recordCount > 0);

  // Checks if the number of displayed Records is less than the number of Records
  // returned from the currently loaded total.
  const canLoadMore = visibleIndex && visibleIndex < sortedRecords.length;

  useEffect(() => {
    if (allRecords) {
      countRecordTypes(allRecords);
      setSortedRecords(allRecords ? sortRecords(allRecords, sorts) : []);

      // If there are enough Records returned to reach the assigned limit
      // and the visibleIndex hasn't been set yet, display "limit" number of Records.
      if (allRecords.length >= limit && !visibleIndex) {
        setVisibleIndex(limit);
      } else if (recordCount < limit && recordsLoaded) {
        // If there are supposed to be less Records than the limit,
        // wait for the CIP/Signals API to finish before showing the Records.
        setVisibleIndex(recordCount);

        // Expand the Record if it's the only Record.
        if (allRecords.length === 1) {
          setExpanded({ [allRecords[0].uid]: true });
        }
      }
    }
  }, [allRecords, recordCount, sorts, limit, recordsLoaded, visibleIndex]);

  const handleCloseSortMenu = () => {
    setSortMenuAnchorEl(null);
  };

  const handleCloseSearchQueryMenu = () => {
    setSearchQueryMenuAnchorEl(null);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const handleExport = (type: ExportFileTypes) => () => {
    saveAs(type === 'csv' ? parseCSV(sortedRecords) : parseJSON(sortedRecords));
    setExportMenuAnchorEl(null);
  };

  const handleLoadMore = (offset: number) => {
    setVisibleIndex((oldIndex) => (oldIndex ? oldIndex + offset : offset));

    if (
      sortedRecords &&
      visibleIndex &&
      sortedRecords.length - visibleIndex === 1
    ) {
      setExpanded({ [sortedRecords[sortedRecords.length - 1].uid]: true });
    }
  };

  const handleRecordExpandCollapse = ({ uid }: RecordType) => {
    setExpanded({ ...expanded, [uid]: !expanded[uid] });
  };

  const handleSortChange = (sorts: RecordSortsType[]) => {
    setSorts(sorts);
  };

  const handleToggleAllRecords = (expand: boolean) => () => {
    sortedRecords?.forEach(({ uid }) => (expanded[uid] = expand));
  };

  const handleToggleSortMenu = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setSortMenuAnchorEl(e.currentTarget);
  };

  const handleSearchQueryMenuItemClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setSearchQueryMenuAnchorEl(e.currentTarget);
  };

  const handleExportMenuItemClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setExportMenuAnchorEl(e.currentTarget);
  };

  const title = `
    ${recordCount.toLocaleString()}
    Record${recordCount !== 1 ? 's' : ''}`;

  const [sortVal] = sorts;

  const startTime = useMemo(() => moment(start).valueOf(), [start]);
  const endTime = useMemo(() => moment(end).valueOf(), [end]);

  const sumoSearchJobUrl = useLAPSearchUrl({
    query: query ?? '',
    startTime,
    endTime,
  });

  const exploreInLogsQueryUrl = useLAPSearchUrl({
    query: exploreInLogsQuery ?? '',
    startTime,
    endTime,
  });

  const searchQueryMenuItemData = [
    {
      label: 'Distinct Aggregated Records',
      description: 'Exact Records evaluated by the Rule',
      disabled: query === exploreInLogsQuery,
      url: sumoSearchJobUrl,
    },
    {
      label: 'All Related Records',
      description: 'All Records in the entire Rule Window',
      url: exploreInLogsQueryUrl,
    },
  ];
  const exportMenuItemData = [
    {
      label: 'CSV',
      fileType: 'csv',
    },
    {
      label: 'JSON',
      fileType: 'json',
    },
  ];

  const { navigationEnabled: kansoModeEnabled } = useKanso();

  return (
    <div className={css.recordsDisplay}>
      <div className={css.recordsHeader} ref={recordsDisplayRef}>
        <Stack
          alignItems='center'
          className={css.recordsHeader}
          direction='row'
          justifyContent='space-between'
        >
          <Typography data-sumo-test='records-title'>{title}</Typography>
          {loading && <Loader display='inline-block' ml={1.875} size='large' />}
        </Stack>

        <Stack direction='row' gap={1}>
          <Tooltip
            title={
              disableSort
                ? 'Sorting is disabled while Records are loading.'
                : null
            }
          >
            <span>
              <Button
                variant='text'
                disabled={disableSort || recordCount <= 1}
                onClick={handleToggleSortMenu}
                startIcon={<SortIcon aria-hidden />}
                endIcon={<AngleDownIcon />}
                color='secondary'
              >
                <span className={css.sortLabel}>
                  <span className={css.sortLabelProp}>
                    {sortProps[sortVal.prop].displayValue}
                  </span>{' '}
                  <>({sortVal.direction?.toLowerCase()}.)</>
                </span>
              </Button>
            </span>
          </Tooltip>

          <Divider flexItem orientation='vertical' />

          <Tooltip
            title={
              !(query && start && end)
                ? 'Log Search is not available for this Signal.'
                : null
            }
          >
            <span>
              <Button
                disabled={!(query && start && end)}
                onClick={handleSearchQueryMenuItemClick}
                endIcon={<AngleDownIcon />}
              >
                View in Log Search
              </Button>
            </span>
          </Tooltip>

          <Tooltip
            title={
              disableSort
                ? 'Exporting is disabled while Records are loading.'
                : null
            }
          >
            <span>
              <Button
                disabled={!recordsLoaded}
                onClick={handleExportMenuItemClick}
                endIcon={<AngleDownIcon />}
                color='secondary'
              >
                Export
              </Button>
            </span>
          </Tooltip>

          <Menu anchorEl={sortMenuAnchorEl} open={sortMenuVisible}>
            <ClickAwayListener onClickAway={handleCloseSortMenu}>
              <Sort onSortChange={handleSortChange} sorts={sorts}>
                <MenuList>
                  {Object.entries(sortProps).map(([name, value]) => (
                    <MenuItem key={name}>
                      <ListItemText
                        primary={
                          <SortHeader prop={name}>
                            {value.displayValue}
                          </SortHeader>
                        }
                      />
                    </MenuItem>
                  ))}
                </MenuList>
              </Sort>
            </ClickAwayListener>
          </Menu>

          <Menu
            anchorEl={searchQueryMenuAnchorEl}
            open={searchQueryMenuVisible}
            sx={{
              [`& a.${linkClasses.root}:link, & a.${linkClasses.root}:visited`]:
                {
                  color: 'text.primary',
                  textDecoration: 'none',
                },
            }}
          >
            <ClickAwayListener onClickAway={handleCloseSearchQueryMenu}>
              <MenuList>
                {searchQueryMenuItemData.map(
                  ({ label, description, disabled, url }) => (
                    <MenuItem
                      key={label}
                      disabled={disabled}
                      onClick={handleCloseSearchQueryMenu}
                      component={Link}
                      data-metric-id={label}
                      href={url}
                      rel='noopener'
                      target='_blank'
                      variant='a'
                    >
                      <ListItemIcon>
                        <ExternalLinkIcon aria-label='open in a new tab' />
                      </ListItemIcon>
                      <ListItemText primary={label} secondary={description} />
                    </MenuItem>
                  )
                )}
              </MenuList>
            </ClickAwayListener>
          </Menu>

          <Menu anchorEl={exportMenuAnchorEl} open={exportMenuVisible}>
            <ClickAwayListener onClickAway={handleCloseExportMenu}>
              <MenuList>
                {exportMenuItemData.map(({ label, fileType }) => (
                  <MenuItem
                    key={label}
                    onClick={handleExport(fileType as ExportFileTypes)}
                  >
                    <ListItemText primary={label} />
                  </MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Menu>
        </Stack>
      </div>
      <Divider sx={{ mt: 1.625, mb: 4 }} />
      {sortedRecords && (
        <RecordDetails
          expanded={expanded}
          onExpandCollapse={handleRecordExpandCollapse}
          records={sortedRecords}
          entityTypeFieldsAndIndicator={entityTypeFieldsAndIndicator}
          visibleIndex={visibleIndex || 0}
        />
      )}
      {canLoadMore && (
        <Stack alignItems='center' mt={0.75}>
          <Button
            onClick={() => {
              handleLoadMore(
                visibleIndex + limit <= sortedRecords.length
                  ? limit
                  : sortedRecords.length - visibleIndex
              );
            }}
            color='secondary'
          >
            Load More
          </Button>
        </Stack>
      )}
      {/* Show the loader if the initial batch of Records haven't been returned yet,
        or if it hasn't finished fetching every Record from the APIs and
        there's not enough Records to do another load */}
      {(!sortedRecords.length || (!recordsLoaded && !canLoadMore)) && (
        <Loader size='xlarge' />
      )}
      {!sortedRecords && 'Invalid records.'}
      {!kansoModeEnabled && (
        <GlobalHotKeys
          handlers={{
            COLLAPSE_ALL: handleToggleAllRecords(false),
            EXPAND_ALL: handleToggleAllRecords(true),
          }}
          keyMap={{
            COLLAPSE_ALL: {
              action: 'keydown',
              name: 'Collapse Records',
              sequence: 'shift+c',
            },
            EXPAND_ALL: {
              action: 'keydown',
              name: 'Expand Records',
              sequence: 'shift+e',
            },
          }}
        />
      )}
    </div>
  );
}
